import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAudit } from '../context/AuditContext';
import { auditPath } from '../utils/auditRoutes';
import { PageFrame } from '../components/PageFrame';
import { SectionCard } from '../components/SectionCard';
import { RatingGroup } from '../components/RatingGroup';
import { TechStackSection } from '../components/TechStackSection';
import { NavigationControls } from '../components/NavigationControls';
import { SectionRecapCard } from '../components/SectionRecapCard';
import { Network, LayoutDashboard, CheckSquare, Lightbulb } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Button } from '../components/ui/button';

const questions = [
  'We struggle to make data-driven decisions quickly.',
  'Information is siloed across teams or departments.',
  'We lack visibility into how our operations are actually performing.',
  'Team members work in different tools that don\'t talk to each other.',
  'Leadership doesn\'t have a unified dashboard or single source of truth.'
];

const readinessItems = [
  { key: 'leadership_understands_ai', label: 'Leadership understands what AI can and cannot do' },
  { key: 'trustworthy_accessible_data', label: 'We have data that is trustworthy and accessible' },
  { key: 'open_to_testing', label: 'We are open to testing one small workflow before expanding' },
  { key: 'budget_or_resources', label: 'We have budget or resources earmarked for change' },
  { key: 'ai_policy_exists', label: 'We have clear rules for how staff may use AI tools' },
  { key: 'current_ai_use_known', label: 'We know whether staff are already using ChatGPT, Copilot, Gemini, Claude, or similar tools' }
];

const riskItems = [
  { key: 'sensitive_data', label: 'This workflow may use sensitive client, employee, financial, legal, tax, insurance, or health information' },
  { key: 'customer_messages', label: 'This workflow may draft client-facing messages' },
  { key: 'money_or_commitments', label: 'This workflow may affect quotes, invoices, pricing, advice, or commitments' },
  { key: 'confidentiality_boundary_needed', label: 'We need clear rules for what information AI tools may not touch without approval' },
  { key: 'reputation_risk_if_wrong', label: 'A mistake in this workflow could damage client trust or reputation' }
];

export default function TeamPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { auditData, updateSectionRating, updateSectionNotes, updateReadinessInput, updateRiskInput } = useAudit();
  const section = auditData.sections.team;
  const readinessInputs = auditData.readiness_inputs || {};
  const riskInputs = auditData.risk_inputs || {};
  const [showValidationDialog, setShowValidationDialog] = useState(false);

  const validateReflection = () => {
    const notes = section.notes || '';
    if (notes.trim().length < 50) {
      setShowValidationDialog(true);
      return false;
    }
    return true;
  };

  const handleNext = () => {
    navigate(auditPath('/roi', location.pathname));
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
  };

  return (
    <PageFrame pageNumber={6} backgroundStyle="light">
      <div className="container mx-auto px-6 py-12 bg-gray-50/50">
        <div className="max-w-5xl mx-auto space-y-6">
          <div className="bg-primary text-white rounded-lg p-6 text-center mb-6">
            <h1 className="text-2xl sm:text-3xl font-extrabold uppercase tracking-wide w-full sm:w-auto mx-auto" data-testid="section-title">
              Section 4 – Team & Decision-Making
            </h1>
          </div>

          <SectionCard
            title="Rate Each Statement"
            description="How true is each statement for your business?"
            icon={Network}
          >
            <div className="space-y-8">
              {questions.map((question, index) => (
                <div key={index} className="pb-6 border-b border-muted last:border-0">
                  <RatingGroup
                    value={section.ratings[index]}
                    onChange={(rating) => updateSectionRating('team', index, rating)}
                    questionId={`team-q${index + 1}`}
                    label={`${index + 1}. ${question}`}
                  />
                </div>
              ))}
            </div>
          </SectionCard>

          <div className="flex items-center justify-between bg-muted rounded-lg p-4 mt-4">
            <span className="text-secondary font-semibold">Section Total:</span>
            <span className="text-3xl font-bold text-primary" data-testid="team-total">
              {section.total} <span className="text-base text-muted-foreground">/ 25</span>
            </span>
          </div>

          <SectionRecapCard
            sectionKey="team"
            section={section}
            tools={auditData.tech_stack?.team?.tools || []}
            integrationRating={auditData.tech_stack?.team?.integration_rating || 0}
          />

          <div className="bg-primary text-white rounded-lg p-6 mt-6">
            <div className="flex items-start gap-4">
              <Lightbulb className="h-8 w-8 flex-shrink-0 mt-1" strokeWidth={1.5} />
              <div>
                <h3 className="font-bold text-lg mb-2">Insight</h3>
                <p>
                  High scores point to places where better visibility, handoffs, and decision support may help. The safest first pilot should support judgment, not replace it.
                </p>
              </div>
            </div>
          </div>

          <SectionCard title="Reflection" icon={LayoutDashboard} className="mt-6">
            <div className="space-y-2">
              <Label htmlFor="team-notes">What decision-making or coordination challenge slows your team down? (Optional)</Label>
              <Textarea
                id="team-notes"
                value={section.notes}
                onChange={(e) => updateSectionNotes('team', e.target.value)}
                placeholder="Share your thoughts here..."
                className="min-h-32 resize-y"
                data-testid="team-notes-textarea"
              />
              <p className="text-xs text-muted-foreground">Optional. More detail helps us personalize the report.</p>
            </div>
          </SectionCard>

          <div className="bg-accent/20 border-2 border-accent rounded-lg p-6 mt-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <CheckSquare className="h-6 w-6 text-secondary" strokeWidth={1.5} />
                <h3 className="text-secondary font-bold text-lg">Readiness and current AI use</h3>
              </div>
              <div className="space-y-3">
                {readinessItems.map((item, index) => (
                  <div key={item.key} className="flex items-start gap-3 p-3 bg-white rounded-lg">
                    <Checkbox
                      id={`readiness-${index}`}
                      className="mt-0.5"
                      checked={Boolean(readinessInputs[item.key])}
                      onCheckedChange={(checked) => updateReadinessInput(item.key, Boolean(checked))}
                      data-testid={`readiness-checkbox-${index}`}
                    />
                    <Label
                      htmlFor={`readiness-${index}`}
                      className="text-foreground cursor-pointer font-normal leading-relaxed"
                    >
                      {item.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white border border-muted rounded-lg p-6 mt-6">
            <div className="space-y-4">
              <h3 className="text-secondary font-bold text-lg">Human review guardrails</h3>
              <p className="text-sm text-muted-foreground">These answers help shape the risk flags in your report. People still make the final decisions.</p>
              <div className="space-y-3">
                <Label htmlFor="approval-owner">Who should approve the first AI-assisted workflow?</Label>
                <Input
                  id="approval-owner"
                  value={riskInputs.approval_owner || ''}
                  onChange={(e) => updateRiskInput('approval_owner', e.target.value)}
                  placeholder="Owner, manager, or team lead"
                  data-testid="approval-owner-input"
                />
              </div>
              <div className="space-y-3">
                {riskItems.map((item, index) => (
                  <div key={item.key} className="flex items-start gap-3 p-3 bg-muted/40 rounded-lg">
                    <Checkbox
                      id={`risk-${index}`}
                      className="mt-0.5"
                      checked={Boolean(riskInputs[item.key])}
                      onCheckedChange={(checked) => updateRiskInput(item.key, Boolean(checked))}
                      data-testid={`risk-checkbox-${index}`}
                    />
                    <Label htmlFor={`risk-${index}`} className="text-foreground cursor-pointer font-normal leading-relaxed">
                      {item.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6">
            <TechStackSection category="team" />
          </div>

          <div className="pt-4">
            <NavigationControls currentPage={6} customNext={handleNext} />
          </div>

          <AlertDialog open={showValidationDialog} onOpenChange={setShowValidationDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Reflection Required</AlertDialogTitle>
                <AlertDialogDescription>
                  Please share your thoughts about team and decision-making challenges (at least 50 characters). This helps us generate a more personalized and valuable report for you.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <Button onClick={() => setShowValidationDialog(false)}>
                  Got it
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </PageFrame>
  );
}